import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { parseApiDate } from './dates';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function safeArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

export function formatCurrency(
  amount: number | string,
  currencyCode: string = 'UGX',
): string {
  return `${currencyCode} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function abbreviateNumber(value: number, decimals = 1): string {
  if (!Number.isFinite(value)) return '0';
  const sign = value < 0 ? '-' : '';
  let abs = Math.abs(value);
  if (abs < 1000) return `${sign}${abs}`;
  const units = ['k', 'M', 'B', 'T'];
  let unit = -1;
  while (abs >= 1000 && unit < units.length - 1) {
    abs /= 1000;
    unit++;
  }
  const num = Number.parseFloat(abs.toFixed(decimals));
  const str = Number.isInteger(num) ? String(Math.trunc(num)) : String(num);
  return `${sign}${str}${units[unit]}`;
}

export function formatCompactCurrency(
  amount: number | string,
  currencyCode: string = 'UGX',
  decimals = 1,
): string {
  return `${currencyCode} ${abbreviateNumber(Number(amount), decimals)}`;
}

/** Format an API date as e.g. "Jun 12, 2026". A YYYY-MM-DD calendar date prints as
 *  written, whatever the viewer's timezone (see parseApiDate). */
export function formatYmdDate(date: string): string {
  return parseApiDate(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Constrain a caller-supplied post-login redirect to a path inside this app.
 *
 *  `navigate()` is not a safe sink for an unvalidated query parameter. A value
 *  starting `//` (or `\\`, which browsers fold to `/`) is read as a
 *  protocol-relative URL and sends the user off-origin, which on a login page
 *  is a credential-phishing vector: the victim authenticates for real, then
 *  lands on an attacker page. react-router has shipped several CVEs in this
 *  area, so the check lives here rather than resting on the library version.
 *
 *  Accepts only a single-slash absolute path; anything else falls back. */
export function safeRedirectPath(
  value: string | null | undefined,
  fallback = '/dashboard',
): string {
  if (!value) return fallback;
  const decoded = (() => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  })();
  const normalized = decoded.replace(/\\/g, '/');
  return /^\/(?!\/)/.test(normalized) ? normalized : fallback;
}

/** Today's date as YYYY-MM-DD, for capping date inputs and rejecting future dates. */
export function todayIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
